import { Link, useLocation, useNavigate } from "react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-provider";
import { useMode } from "@/lib/mode-provider";
import { visibleNav, isItemActive, type Role } from "@/lib/nav";
import { GraduationCap, LogOut } from "lucide-react";

export function AppSidebar() {
  const { profile, signOut } = useAuth();
  const { mode, toggleMode } = useMode();
  const location = useLocation();
  const navigate = useNavigate();

  const groups = visibleNav(profile?.role as Role | undefined);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" tooltip="CS Training Center">
              <Link to="/" className="flex items-center gap-2">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <GraduationCap className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 text-left text-sm leading-none">
                  <span className="font-semibold">CS Training</span>
                  <span className="text-xs text-muted-foreground">Center</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isItemActive(item, location.pathname);
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                        <Link to={item.to}>
                          <Icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={toggleMode} tooltip={`Switch to ${mode === "training" ? "Show All" : "Training"}`}>
              <span aria-hidden className="text-base leading-none">{mode === "training" ? "🎓" : "👀"}</span>
              <span className="text-xs">
                Mode: <span className="font-medium">{mode === "training" ? "Training" : "Show All"}</span>
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton size="lg" tooltip={profile?.name ?? "Account"}>
              <div className="flex size-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                {(profile?.name ?? "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex flex-col gap-0.5 text-left text-sm leading-none">
                <span className="truncate font-medium">{profile?.name ?? "Loading..."}</span>
                <span className="text-xs text-muted-foreground capitalize">
                  {profile?.role ?? ""}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleSignOut} tooltip="Sign out">
              <LogOut />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="hidden md:block px-2 pb-1 pt-0.5">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="h-5 w-full justify-start px-1 text-[10px] text-muted-foreground hover:text-foreground"
            onClick={() => navigate(-1)}
            title="Go back"
          >
            ← Back
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
